import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { extractNeedRequirements, updateNeed } from '@/lib/recruitmentApi';
import apiClient from '@/lib/api';
import CompanySelectCombobox from '@/components/admin/recruitment/CompanySelectCombobox';
import { AlertTriangle, Sparkles } from 'lucide-react';

const CONTRACT_OPTIONS = ['CDI', 'Freelance', 'Portage'];

const split = (value) => (value || '').split(',').map((s) => s.trim()).filter(Boolean);
const inputClass =
  'w-full min-h-11 rounded-lg bg-slate-900 border border-white/20 px-3 py-2 text-sm text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400';

export default function NeedEditModal({
  isOpen,
  onClose,
  need,
  onUpdated,
  applicationsCount = 0,
}) {
  const [form, setForm] = useState({
    title: '',
    client_id: '',
    description: '',
    location: '',
    seniority: '',
    experience_years: '',
    constraints: '',
    start_date: '',
    end_date: '',
    status: 'open',
    mandatory: '',
    recommended: '',
    bonus: '',
    functional: '',
    responsibilities: '',
    context_technologies: '',
  });

  const [contracts, setContracts] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [analysisError, setAnalysisError] = useState('');
  const [extracted, setExtracted] = useState(null);

  // Commercial terms & company selection
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [tjmClient, setTjmClient] = useState('');
  const [tjmClientMode, setTjmClientMode] = useState('unknown');
  const [freeDays, setFreeDays] = useState(0);
  const [missionType, setMissionType] = useState('long');
  const [isStartDateProvisional, setIsStartDateProvisional] = useState(false);

  // Safeguard: company change confirmation
  const [companyChangeConfirmed, setCompanyChangeConfirmed] = useState(false);

  useEffect(() => {
    if (!isOpen || !need) return;

    // Load companies defensively
    if (apiClient && typeof apiClient.get === 'function') {
      const p = apiClient.get('/recruitment/companies');
      if (p && typeof p.then === 'function') {
        p.then((res) => {
          setCompanies(Array.isArray(res?.data) ? res.data : []);
        }).catch(() => setCompanies([]));
      }
    }

    // Prepopulate form from existing need
    const reqs = need.requirements || {};
    setForm({
      title: need.title || '',
      client_id: need.client_id || need.company_name || '',
      description: need.description || need.description_raw || '',
      location: need.location || '',
      seniority: need.seniority || '',
      experience_years: need.experience_years || '',
      constraints: need.constraints || '',
      start_date: need.start_date || '',
      end_date: need.end_date || '',
      status: need.status || 'open',
      mandatory: (reqs.mandatory || need.required_skills || []).join(', '),
      recommended: (reqs.recommended || []).join(', '),
      bonus: (reqs.bonus || []).join(', '),
      functional: (reqs.functional || []).join(', '),
      responsibilities: (reqs.responsibilities || []).join(', '),
      context_technologies: (reqs.context_technologies || []).join(', '),
    });

    setContracts(need.contract_types || (need.contract_type ? [need.contract_type] : []));
    setSelectedCompanyId(need.company_id || need.client_id || '');
    setSelectedContactId(need.client_contact_id || '');

    const comm = need.commercial_terms || {};
    const tjmVal = need.tjm_client != null ? need.tjm_client : comm.tjm_client;
    setTjmClient(tjmVal != null ? String(tjmVal) : '');
    setTjmClientMode(need.tjm_client_mode || comm.mode || (tjmVal ? 'fixed' : 'unknown'));
    setFreeDays(need.free_days != null ? need.free_days : (comm.free_days || 0));
    setMissionType(need.mission_type || comm.mission_type || 'long');
    setIsStartDateProvisional(Boolean(need.is_start_date_provisional));
    setCompanyChangeConfirmed(false);
    setError('');
    setAnalysisError('');
  }, [isOpen, need]);

  if (!isOpen || !need) return null;

  const originalCompanyId = need.company_id || need.client_id || '';
  const isCompanyChanged = Boolean(
    applicationsCount > 0 &&
    selectedCompanyId &&
    originalCompanyId &&
    selectedCompanyId !== originalCompanyId
  );

  const analyzeDescription = async () => {
    if (!form.description || !form.description.trim()) {
      setAnalysisError("Veuillez renseigner un descriptif de mission avant de lancer l’analyse.");
      return;
    }
    setBusy(true);
    setAnalysisError('');
    setError('');
    try {
      const data = await extractNeedRequirements(form.description, form.title);
      setExtracted(data);
      setForm((prev) => {
        const next = { ...prev };
        // RÈGLE MÉTIER ABSOLUE : L'intitulé saisi par le recruteur ne doit JAMAIS être écrasé
        if (!next.title || !next.title.trim()) {
          if (data.title) next.title = String(data.title);
        }
        if (data.location && !next.location) next.location = String(data.location);
        if (data.seniority && !next.seniority) next.seniority = String(data.seniority);
        if (data.mandatory?.length) next.mandatory = data.mandatory.join(', ');
        if (data.recommended?.length) next.recommended = data.recommended.join(', ');
        if (data.bonus?.length) next.bonus = data.bonus.join(', ');
        if (data.functional?.length) next.functional = data.functional.join(', ');
        if (data.responsibilities?.length) next.responsibilities = data.responsibilities.join(', ');
        if (data.context_technologies?.length) next.context_technologies = data.context_technologies.join(', ');
        return next;
      });
      setExpanded(true);
    } catch {
      setAnalysisError("L’analyse IA n’a pas abouti. Vous pouvez ajuster les critères manuellement.");
    } finally {
      setBusy(false);
    }
  };

  const handleSelectCompany = (compId, companyObj) => {
    setSelectedCompanyId(compId);
    if (companyObj) {
      setForm((prev) => ({ ...prev, client_id: companyObj.name }));
    } else {
      setForm((prev) => ({ ...prev, client_id: '' }));
      setSelectedContactId('');
    }
  };

  const handleCompanyCreated = (newComp) => {
    setCompanies((prev) => [newComp, ...prev.filter((c) => (c.id || c._id) !== (newComp.id || newComp._id))]);
    setSelectedCompanyId(newComp.id || newComp._id);
    setForm((prev) => ({ ...prev, client_id: newComp.name }));
  };

  const handleContactCreated = (newContact, compId) => {
    setCompanies((prev) =>
      prev.map((comp) => {
        if ((comp.id || comp._id) === compId) {
          const existingContacts = Array.isArray(comp.contacts) ? comp.contacts : [];
          return { ...comp, contacts: [...existingContacts, newContact] };
        }
        return comp;
      })
    );
    setSelectedContactId(newContact.id || newContact._id);
  };

  const selectedCompany = companies.find((c) => (c.id || c._id) === selectedCompanyId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.title.trim()) {
      setError("L’intitulé du poste est obligatoire.");
      return;
    }

    if (missionType === 'court' && !form.end_date) {
      setError("Pour une mission courte, la date de fin est obligatoire pour le calcul sur la période réelle.");
      return;
    }

    if (isCompanyChanged && !companyChangeConfirmed) {
      setError("Veuillez confirmer explicitement le changement d'entreprise cliente pour ce besoin ayant déjà des candidatures associées.");
      return;
    }

    const freeDaysInt = Math.max(0, parseInt(freeDays, 10) || 0);

    setSubmitting(true);
    try {
      const requirements = Object.fromEntries(
        ['mandatory', 'recommended', 'bonus', 'functional', 'responsibilities', 'context_technologies'].map((key) => [
          key,
          split(form[key]),
        ])
      );
      if (extracted?.role_identified || need.requirements?.role_identified) {
        requirements.role_identified = extracted?.role_identified || need.requirements?.role_identified;
      }

      const payload = {
        title: form.title.trim(),
        client_id: form.client_id.trim() || null,
        company_id: selectedCompanyId || null,
        company_name: form.client_id.trim() || null,
        company_type: selectedCompany?.company_type || need.company_type || 'CLIENT',
        client_contact_id: selectedContactId || null,
        description: form.description.trim() || null,
        description_raw: form.description.trim() || null,
        location: form.location.trim() || null,
        seniority: form.seniority.trim() || null,
        experience_years: form.experience_years.trim() || null,
        constraints: form.constraints.trim() || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        is_start_date_provisional: isStartDateProvisional,
        status: form.status || 'open',
        contract_types: contracts.length > 0 ? contracts : null,
        contract_type: contracts.length > 0 ? contracts[0] : null,
        requirements,
        requirements_source: 'manual',
        tjm_client: tjmClient && tjmClientMode !== 'unknown' ? parseFloat(tjmClient) : null,
        tjm_client_mode: tjmClientMode,
        free_days: freeDaysInt,
        mission_type: missionType,
      };

      const updated = await updateNeed(need._id, payload);
      if (onUpdated) {
        onUpdated(updated);
      }
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Échec de l'enregistrement des modifications.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl bg-[#101426] text-white border-white/10 rounded-2xl w-[calc(100%_-_2rem)] max-h-[90vh] overflow-y-auto">
        <DialogTitle className="pr-10 text-lg font-bold flex items-center gap-2">
          <span>Modifier le besoin client</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 font-mono">
            {need.title}
          </span>
        </DialogTitle>
        <DialogDescription className="text-xs text-slate-400">
          Modifiez les informations, critères techniques, conditions commerciales ou interlocuteur sans altérer les candidatures rattachées.
        </DialogDescription>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2" data-testid="need-edit-form">
          {error && (
            <div className="p-3 border border-rose-500/40 bg-rose-500/10 rounded-xl text-xs text-rose-300 flex items-center gap-2" role="alert">
              <AlertTriangle className="h-4 w-4 shrink-0 text-rose-400" />
              <span>{error}</span>
            </div>
          )}

          {/* Description & AI re-analysis */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-300">
              Descriptif de mission
            </label>
            <textarea
              className={inputClass}
              rows={4}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Descriptif de la mission..."
              disabled={busy || submitting}
            />
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={analyzeDescription}
                disabled={busy || submitting || !form.description.trim()}
                className="min-h-10 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white disabled:opacity-50 flex items-center gap-1.5"
                data-testid="re-analyze-btn"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {busy ? "Analyse en cours..." : "Réanalyser le descriptif"}
              </button>
              <span className="text-[11px] text-slate-400">
                L’analyse actualise les compétences sans modifier l’intitulé déjà saisi.
              </span>
            </div>
            {analysisError && (
              <p className="text-xs text-amber-300 bg-amber-500/10 p-2.5 rounded-lg border border-amber-500/20">
                {analysisError}
              </p>
            )}
          </div>

          {/* Entreprise & Contact Client */}
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-4">
            <CompanySelectCombobox
              companies={companies}
              selectedCompanyId={selectedCompanyId}
              onSelectCompany={handleSelectCompany}
              selectedContactId={selectedContactId}
              onSelectContact={setSelectedContactId}
              onCompanyCreated={handleCompanyCreated}
              onContactCreated={handleContactCreated}
              disabled={busy || submitting}
            />

            {/* Interlocuteur client */}
            {selectedCompany && (
              <div className="pt-2 border-t border-white/5">
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  Interlocuteur au sein de l’entreprise
                </label>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedContactId}
                    onChange={(e) => setSelectedContactId(e.target.value)}
                    className={inputClass}
                    disabled={busy || submitting}
                  >
                    <option value="">Sélectionner un interlocuteur (facultatif)</option>
                    {(selectedCompany.contacts || []).map((ct, idx) => (
                      <option key={ct.id || ct._id || idx} value={ct.id || ct._id || ct.name}>
                        {ct.name} {ct.role ? `(${ct.role})` : ''} {ct.email ? `— ${ct.email}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Safeguard: Company change warning */}
            {isCompanyChanged && (
              <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-200 text-xs space-y-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-semibold text-white">Changement d’entreprise cliente détecté</p>
                    <p className="mt-0.5 text-slate-300">
                      Ce besoin comporte déjà <strong>{applicationsCount}</strong> candidature(s) positionnée(s) ou en cours.
                      Changer l’entreprise mettra à jour la fiche mais <strong>ne réattribuera pas silencieusement</strong> l’historique des présentations et entretiens existants.
                    </p>
                  </div>
                </div>
                <label className="flex items-center gap-2 pt-1 font-semibold text-amber-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={companyChangeConfirmed}
                    onChange={(e) => setCompanyChangeConfirmed(e.target.checked)}
                    className="rounded border-amber-500/50 bg-slate-900"
                  />
                  <span>Je confirme vouloir modifier l'entreprise rattachée à ce besoin</span>
                </label>
              </div>
            )}
          </div>

          {/* Conditions Commerciales & Rentabilité */}
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-blue-400">
              Conditions Commerciales & Rentabilité
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-[11px] text-slate-400 mb-1 font-semibold">
                  Type de mission
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setMissionType('long')}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-all ${
                      missionType === 'long'
                        ? 'bg-blue-600 text-white border-blue-500 shadow-sm'
                        : 'bg-slate-900 text-slate-300 border-white/10 hover:bg-white/5'
                    }`}
                  >
                    Mission longue
                    <span className="block text-[10px] font-normal opacity-80">Horizon 12 mois</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMissionType('court')}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-all ${
                      missionType === 'court'
                        ? 'bg-blue-600 text-white border-blue-500 shadow-sm'
                        : 'bg-slate-900 text-slate-300 border-white/10 hover:bg-white/5'
                    }`}
                  >
                    Mission courte
                    <span className="block text-[10px] font-normal opacity-80">Période ferme</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[11px] text-slate-400 mb-1 font-semibold">
                  Nombre de jours de gratuité
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={freeDays}
                  onChange={(e) => setFreeDays(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  className={inputClass}
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Déduit une seule fois sur toute la durée de la mission.
                </p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-[11px] text-slate-400 mb-1 font-semibold">
                  Mode TJM Client
                </label>
                <select
                  value={tjmClientMode}
                  onChange={(e) => setTjmClientMode(e.target.value)}
                  className={inputClass}
                >
                  <option value="unknown">Non renseigné</option>
                  <option value="fixed">TJM fixe</option>
                  <option value="max">TJM maximum</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] text-slate-400 mb-1 font-semibold">
                  Montant TJM Client (€/j)
                </label>
                <input
                  type="number"
                  min="0"
                  step="10"
                  placeholder="Ex: 650"
                  disabled={tjmClientMode === 'unknown'}
                  value={tjmClient}
                  onChange={(e) => setTjmClient(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Critères Détaillés */}
          <details
            open={expanded}
            onToggle={(e) => setExpanded(e.currentTarget.open)}
            className="border border-white/15 rounded-xl p-4 space-y-3"
          >
            <summary className="cursor-pointer min-h-11 text-xs font-semibold text-slate-200">
              Critères du besoin (Intitulé, Dates, Compétences, Statut)
            </summary>

            {extracted?.suggested_title && form.title && extracted.suggested_title !== form.title && (
              <div className="p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300 flex items-center justify-between">
                <span>Suggestion IA de titre alternatif : <strong className="text-white">{extracted.suggested_title}</strong></span>
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, title: extracted.suggested_title }))}
                  className="px-2 py-1 text-[11px] rounded bg-blue-600 hover:bg-blue-500 text-white font-medium"
                >
                  Appliquer
                </button>
              </div>
            )}

            {(extracted?.role_identified || need.requirements?.role_identified) && (
              <div className="text-[11px] text-slate-400">
                Rôle identifié : <span className="font-semibold text-purple-300">{extracted?.role_identified || need.requirements?.role_identified}</span>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2 pt-2">
              <label className="block space-y-1 text-xs text-slate-300 sm:col-span-2">
                <span className="font-medium text-white">Intitulé du poste * (Référence stricte)</span>
                <input
                  className={inputClass}
                  type="text"
                  value={form.title}
                  disabled={busy || submitting}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex: Chef de projet"
                  data-testid="edit-title-input"
                />
              </label>

              {[
                ['location', 'Localisation'],
                ['seniority', 'Séniorité'],
                ['experience_years', 'Expérience souhaitée'],
                ['responsibilities', 'Responsabilités principales'],
                ['mandatory', 'Technologies obligatoires'],
                ['recommended', 'Technologies nécessaires ou recommandées'],
                ['bonus', 'Technologies bonus'],
                ['context_technologies', 'Technologies de contexte (environnement)'],
                ['functional', 'Compétences métier'],
                ['constraints', 'Contraintes particulières'],
              ].map(([key, label]) => (
                <label key={key} className="block space-y-1 text-xs text-slate-300">
                  <span className="font-medium">{label}</span>
                  <input
                    className={inputClass}
                    type="text"
                    value={form[key]}
                    disabled={busy || submitting}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  />
                </label>
              ))}

              {/* Start Date */}
              <div className="space-y-1 text-xs text-slate-300">
                <label className="block font-medium">Date de démarrage souhaitée</label>
                <input
                  className={inputClass}
                  type="date"
                  value={form.start_date}
                  disabled={busy || submitting}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
                <label className="flex items-center gap-2 pt-1 text-[11px] text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isStartDateProvisional}
                    onChange={(e) => setIsStartDateProvisional(e.target.checked)}
                    className="rounded border-slate-700 bg-slate-900"
                  />
                  <span>Date prévisionnelle à confirmer</span>
                </label>
              </div>

              {/* End Date */}
              <div className="space-y-1 text-xs text-slate-300">
                <label className="block font-medium">
                  Date de fin {missionType === 'court' ? '*' : '(optionnelle)'}
                </label>
                <input
                  className={inputClass}
                  type="date"
                  value={form.end_date}
                  disabled={busy || submitting}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </div>

              {/* Status */}
              <div className="space-y-1 text-xs text-slate-300">
                <label className="block font-medium">Statut du besoin</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className={inputClass}
                  disabled={busy || submitting}
                >
                  <option value="open">Ouvert (open)</option>
                  <option value="in_progress">En cours</option>
                  <option value="won">Pourvu / Gagné</option>
                  <option value="lost">Perdu</option>
                  <option value="cancelled">Annulé</option>
                </select>
              </div>

              {/* Types de contrat */}
              <fieldset className="space-y-1 text-xs text-slate-300 sm:col-span-2">
                <legend className="font-medium text-slate-200">Types de contrat acceptés</legend>
                <div className="flex flex-wrap gap-4 pt-1">
                  {CONTRACT_OPTIONS.map((c) => (
                    <label key={c} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={contracts.includes(c)}
                        disabled={busy || submitting}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setContracts([...contracts, c]);
                          } else {
                            setContracts(contracts.filter((item) => item !== c));
                          }
                        }}
                        className="rounded border-slate-700 bg-slate-900"
                      />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
          </details>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2.5 rounded-xl border border-white/15 text-xs font-semibold text-slate-300 hover:bg-white/5 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || busy}
              className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white shadow-lg shadow-blue-600/20 disabled:opacity-50 transition-all flex items-center gap-2"
              data-testid="save-need-edit-btn"
            >
              {submitting ? "Enregistrement..." : "Enregistrer les modifications"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
